<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="Replace Document"
    :style="{ width: '28rem' }"
  >
    <div class="flex flex-col gap-6">
      <div class="rounded-md bg-amber-50 p-4">
        <div class="flex items-start gap-3">
          <i class="pi pi-exclamation-triangle text-amber-600"></i>
          <div class="text-sm text-amber-800">
            <p class="font-semibold">Warning</p>
            <p>
              Replacing this document will remove all existing signatures. This
              action cannot be undone.
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-gray-900">
          Current Document
        </label>
        <p class="text-sm text-gray-600">{{ document?.name }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-gray-900">
          New Document
        </label>
        <FileUpload
          mode="basic"
          :auto="false"
          :custom-upload="true"
          accept=".pdf"
          choose-label="Select PDF"
          :disabled="loading"
          @select="onFileSelect"
        />
        <small v-if="selectedFile" class="text-xs text-gray-600">
          {{ selectedFile.name }}
        </small>
        <small v-if="error" class="text-xs text-red-600">
          {{ error }}
        </small>
      </div>

      <div class="flex justify-end gap-3">
        <Button
          type="button"
          label="Cancel"
          severity="secondary"
          class="rounded-md px-4 py-2 font-semibold"
          :disabled="loading"
          @click="close"
        />
        <Button
          type="button"
          label="Replace Document"
          severity="warn"
          :loading="loading"
          :disabled="!selectedFile"
          class="rounded-md px-4 py-2 font-semibold"
          @click="handleReplace"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { DirectUpload } from "@rails/activestorage"
import Button from "primevue/button"
import Dialog from "primevue/dialog"
import FileUpload, { type FileUploadSelectEvent } from "primevue/fileupload"
import { ref, watch } from "vue"
import { apolloClient } from "../queries/client"
import REPLACE_DOCUMENT from "../queries/document/replace.gql"

interface DocumentType {
  id: string
  gid: string
  name: string
}

const props = defineProps<{
  document: DocumentType | null
}>()

const emit = defineEmits<{
  success: []
}>()

const visible = defineModel<boolean>("visible", { required: true })

const selectedFile = ref<File | null>(null)
const loading = ref(false)
const error = ref("")

watch(visible, (isVisible) => {
  if (isVisible) {
    selectedFile.value = null
    error.value = ""
  }
})

const close = () => {
  visible.value = false
}

const onFileSelect = (event: FileUploadSelectEvent) => {
  selectedFile.value = event.files[0] as File
  error.value = ""
}

const handleReplace = async () => {
  if (!selectedFile.value || !props.document) return

  loading.value = true
  error.value = ""

  try {
    const fileId = await uploadFile(selectedFile.value)

    const result = await apolloClient.mutate({
      mutation: REPLACE_DOCUMENT,
      variables: {
        documentId: props.document.gid,
        fileId,
      },
    })

    if (result?.data?.replaceDocument?.errors?.length) {
      error.value = result.data.replaceDocument.errors.join(", ")
      return
    }

    close()
    emit("success")
  } catch (e) {
    console.error("Replace error:", e)
    error.value = "Something went wrong, please try again."
  } finally {
    loading.value = false
  }
}

const uploadFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const upload = new DirectUpload(
      file,
      "/rails/active_storage/direct_uploads",
    )
    upload.create((err, blob) => {
      if (err) {
        reject(err)
      } else {
        resolve(blob!.signed_id)
      }
    })
  })
</script>
